import {
  CircularProgress,
  DialogActions,
  DialogContent,
} from '@mui/material';
import { issueClient } from 'shared/domain/issue/issueClient';
import Dialog from '@mui/material/Dialog';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys, IssueTranslationKeys, ValidationTranslationKeys } from 'intl/keys';
import { GraphicUploader } from 'components/common/graphicUploader';
import { useGraphicUploader } from 'components/common/graphicUploader/useLocalGraphicUploader';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { useIssueStore } from 'store/issue';
import { createUniqueId } from 'shared/utils/id/id';
import { parseEventDescription } from 'shared/domain/issueEvent/parseEventDescription';
import {
  EventDescriptionPartToViewMapper,
  MentionToViewMapper,
} from 'components/dataProviders/withIssue/eventDescription';
import { IssueEventOnView } from 'components/dataProviders/withIssue/model';
import { useUsers } from 'components/dataProviders/withUsers';
import { ButtonContained, ButtonText } from 'components/general';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useProjectOrganizationId } from 'store/project';
import { DocumentToEventFromUrlBinder } from 'shared/domain/document/documentBinder';
import { usersWithoutSuperAdminSelector } from 'components/dataProviders/withUsers/model';
import { LabelledUserWithSitesAndProcesses } from 'shared/domain/user/mapping/toView';
import { UserOnView } from 'shared/domain/user/types/view';
import { EventDescriptionType } from 'shared/types/eventDescription';
import { FieldSize } from 'shared/types/fieldSize';
import { useManagement } from 'views/issue/issueView/AccessManagement/useManagement';
import useIsMobile from '../../../../hooks/useIsMobile';
import { showChannelRequestErrorToast } from 'services/toaster';
import { useCurrentUserInProjectLabelled } from 'store/user';
import { leaveDialogDispatcher } from '../../../../setup/navigation/NavigationPrompt';
import useFormEdit from '../../forms/useFormEdit';
import {
  useCurrentIssueDocuments,
  useCurrentIssueParams,
  useShownIssue,
} from 'components/dataProviders/withIssue';
import DescriptionInputWithMentions from './DescriptionInputWithMentions';
import {
  dialogActionTypes,
  dialogInitialState,
  dialogReducer,
} from './EventDialogReducer';
import { useStyles } from './EventDialogStyles';
import * as Wrappers from './EventDialogWrappers';
import { ShareableUrlTargetKind } from 'shared/domain/issue/issueShareableUrl';
import { showEventAddedToast, showEventEditedToast } from '../eventToasts';

const MAX_EVENT_TITLE_LENGTH = FieldSize.M;
const MAX_EVENT_DESCRIPTION_LENGTH = FieldSize.XL;

export enum EventDialogActionNames {
  create = 'create',
  edit = 'edit',
}

export interface CreateEventDialogAction {
  actionName: EventDialogActionNames.create;
  params: {
    // issue: IssueOnSingleView;
    title: string;
    initialMentions: UserOnView[] | null;
  };
}
export interface EditEventDialogAction {
  actionName: EventDialogActionNames.edit;
  params: {
    event: IssueEventOnView;
    title: string;
  };
}

type EventDialogAction = EditEventDialogAction | CreateEventDialogAction;

function isEditingEvent(
  action: EventDialogAction
): action is EditEventDialogAction {
  return action?.actionName === EventDialogActionNames.edit;
}

function isCreatingEvent(
  action: EventDialogAction
): action is CreateEventDialogAction {
  return action?.actionName === EventDialogActionNames.create;
}

interface EventDialogProps {
  onClose: () => void;
  isOpen: boolean;
  action: CreateEventDialogAction | EditEventDialogAction;
}

const validateTitleInput = (
  value: string,
  intl: IntlShape,
  action: CreateEventDialogAction | EditEventDialogAction
): string => {
  const fieldLabel = formatTranslation(intl, IssueTranslationKeys.eventsDialogAddTitle);

  if (isCreatingEvent(action) && !value) {
    return '';
  }

  if (value.length < 1) {
    return formatTranslation(intl, ValidationTranslationKeys.inputRequired, {
      prop: fieldLabel,
    });
  }

  if (value.length > MAX_EVENT_TITLE_LENGTH) {
    return formatTranslation(intl, ValidationTranslationKeys.stringLengthMaximumWeb, {
      maxLength: MAX_EVENT_TITLE_LENGTH,
    });
  }

  return '';
};

export const createBaseUrl = (
  issueId: string,
  eventId: string
): string => {
  return `/issue/${issueId}/event/${eventId}`;
};

const EventDialog = ({
  onClose,
  isOpen,
  action,
}: EventDialogProps): JSX.Element | null => {
  const formName = 'issueEvent';
  const [state, setState] = useReducer(dialogReducer, dialogInitialState);
  const createDialog = useDialog();
  const isMobile = useIsMobile();
  const classes = useStyles({ isMobile });

  const { setEditing } = useFormEdit();
  const formDirty = useRef(false);
  const dialogRootElementRef = useRef<HTMLDivElement>(null);

  const intl = useIntl();

  const currentUser = useCurrentUserInProjectLabelled();
  const organizationId = useProjectOrganizationId();
  const {
    all: { items: users },
  } = useUsers();
  const availableUsers = useMemo<LabelledUserWithSitesAndProcesses[]>(
    () => usersWithoutSuperAdminSelector(users, organizationId),
    [users, organizationId]
  );
  const issue = useShownIssue();
  const {
    documents: { eventDocuments, incomingEmailMessageDocuments },
  } = useCurrentIssueDocuments();

  const resync = useIssueStore.use.resyncCurrentIssue();
  const addEventBeingSaved = useIssueStore.use.addEventBeingSaved();
  const markEventBeingSaved = useIssueStore.use.markEventBeingSaved();

  const {
    event,
    charCount,
    eventTitle,
    savingData,
    nameTouched,
    dialogTitle,
    datePristine,
    descriptionError,
    eventDescriptionWithMarkup,
    descriptionResetKey,
  } = state;
  const { managePermissions } = useManagement();

  const issueParams = useCurrentIssueParams();
  const { issueId } = issueParams;
  const eventId = event?._id;
  const visibleDocuments =
    eventDocuments[event?._id] ||
    incomingEmailMessageDocuments[event?._id] ||
    [];

  const baseUrl = useMemo(
    () => createBaseUrl(issueId, eventId),
    [issueId, eventId]
  );

  const fetchShareableUrl = useCallback(
    (documentId: string) =>
      issueClient.getShareableUrl({ kind: ShareableUrlTargetKind.eventDocument, issueId, eventId, documentId }),
    [issueId, eventId]
  );

  const GU = useGraphicUploader(
    visibleDocuments,
    {
      baseUrl,
      file_types: undefined,
      thumbnailSize: 170,
      dataQaPrefix: 'event-dialog-',
      documentBinderFactory: (baseUrl?: string) => {
        if (!baseUrl) throw new Error('From url binder requires URL');
        return new DocumentToEventFromUrlBinder(baseUrl);
      },
      fetchShareableUrl,
    },
    true
  );

  const GUChangesCount = Object.keys(GU.changeSet).length;
  const [GUFilesCount, setGUFilesCount] = useState<number>(0);
  useEffect(() => {
    return GU.filesStore.subscribe(() => {
      setGUFilesCount(GU.filesStore.get().length);
    });
  }, [GU.filesStore]);
  const documentsCount = visibleDocuments.length;

  const graphicUploaderChanged = useMemo(
    () => GUChangesCount !== 0 || GUFilesCount !== documentsCount,
    [GUChangesCount, GUFilesCount, documentsCount]
  );

  useEffect(() => {
    if (!formDirty.current && !datePristine) {
      formDirty.current = true;
      setEditing(true, formName);
    }
  }, [setEditing, datePristine]);

  const closeDialog = useCallback((): void => {
    setEditing(false, formName);
    onClose();
  }, [onClose, setEditing]);

  const generateDescriptionWithMarkup = useCallback(
    (
      descriptionPartMappers: EventDescriptionPartToViewMapper[]
    ): string => {
      return (
        descriptionPartMappers
          ?.map((descriptionPartMapper) => {
            return descriptionPartMapper.toMarkupText();
          })
          .join('') ?? ''
      );
    },
    []
  );

  const handleNameBlur = (): void => {
    if (!state.nameTouched) {
      setState({
        type: dialogActionTypes.SET_NAME_TOUCHED,
        payload: true,
      });
    }
  };

  const handleNameInputChange = (
    event: any,
    value: string | null | undefined
  ): void => {
    if (datePristine) {
      setState({
        type: dialogActionTypes.SET_PRISTINE_STATE,
        payload: false,
      });
    }
    handleNameChange(value || '');
  };

  const handleNameChange = useCallback((value: string): void => {
    setState({ type: dialogActionTypes.SET_EVENT_TITLE, payload: value });
  }, []);

  const prepareForEdit = useCallback(
    (params: EditEventDialogAction['params']): void => {
      const { title, event } = params;

      const eventDescriptionWithMarkup: string =
        generateDescriptionWithMarkup(event?.description || []);

      setState({
        type: dialogActionTypes.PREPARE_FOR_EDIT,
        payload: {
          title,
          event,
          eventDescriptionWithMarkup,
        },
      });

      handleNameChange(params.event.title);
    },
    [generateDescriptionWithMarkup, handleNameChange]
  );

  const prepareForCreation = useCallback(
    (params: CreateEventDialogAction['params']): void => {
      const { title, initialMentions } = params;

      const eventDescriptionWithMarkup: string =
        generateDescriptionWithMarkup(
          initialMentions
            ? initialMentions.map(
                (mention) =>
                  new MentionToViewMapper({
                    type: EventDescriptionType.mention,
                    user: mention,
                    ...issueParams,
                  })
              )
            : []
        );

      setState({
        type: dialogActionTypes.PREPARE_DIALOG,
        payload: { title, eventDescriptionWithMarkup },
      });
    },
    [generateDescriptionWithMarkup, issueParams]
  );

  const handleCancelAddEvent = useCallback((): void => {
    if (!datePristine || graphicUploaderChanged) {
      leaveDialogDispatcher.dispatch('show', {
        onConfirm: closeDialog,
      });

      return;
    }

    closeDialog();
  }, [datePristine, closeDialog, graphicUploaderChanged]);

  const handleDescriptionChange = useCallback((): void => {
    if (datePristine) {
      setState({
        type: dialogActionTypes.SET_PRISTINE_STATE,
        payload: false,
      });
    }
  }, [datePristine]);

  const handleDescriptionBlur = useCallback((value): void => {
    setState({
      type: dialogActionTypes.SET_EVENT_DESCRIPTION,
      payload: value,
    });
  }, []);

  const handleDescriptionError = useCallback((hasError: boolean): void => {
    setState({
      type: dialogActionTypes.SET_EVENT_DESCRIPTION_ERROR,
      payload: hasError,
    });
  }, []);

  const editCurrentEvent = (): Promise<void> => {
    setState({ type: dialogActionTypes.SET_SAVING_DATA, payload: true });

    const uploadAndChangesetPromise = Promise.resolve(
      GU.uploadFiles(baseUrl, undefined)
    ).then(() => Promise.all(GU.executeChangeset()));

    return uploadAndChangesetPromise
      .then(() => {
        return issueClient.editEvent(
          { issueId: issue._id, eventId: event._id },
          eventTitle,
          eventDescriptionWithMarkup ?? ''
        );
      })
      .then((data) => {
        markEventBeingSaved(issue._id, data._id, { modifiedAt: new Date().toISOString() });
      })
      .then(() => {
        showEventEditedToast(eventTitle);
      })
      .catch((error: unknown) => {
        showChannelRequestErrorToast('Error while editing event', error);
      })
      .then(() => {
        setState({
          type: dialogActionTypes.SET_SAVING_DATA,
          payload: false,
        });
        closeDialog();
        issueClient.sync();
        resync({ force: true });
      })
      .then(() =>
        managePermissions({
          eventDescriptionWithMarkup,
          issueManagers: availableUsers,
          issue,
          createDialog,
          organizationId,
        }).catch(() => {})
      );
  };

  const addNewEvent = async (): Promise<void> => {
    setState({ type: dialogActionTypes.SET_SAVING_DATA, payload: true });

    const title =
      eventTitle || formatTranslation(intl, CommonTranslationKeys.untitled);
    const now = new Date().toISOString();
    addEventBeingSaved(issue._id, {
      _id: createUniqueId(),
      title,
      type: event.type ?? '',
      description: parseEventDescription(eventDescriptionWithMarkup ?? ''),
      deleted: false,
      createdAt: now,
      modifiedAt: now,
    });
    return issueClient
      .createEvent(
        issue._id,
        title,
        eventDescriptionWithMarkup ?? ''
      )
      .then((response) => {
        return Promise.resolve(
          GU.uploadFiles(createBaseUrl(issueId, response._id), undefined)
        ).then(() => Promise.all(GU.executeChangeset()));
      })
      .then(() => {
        showEventAddedToast(eventTitle);
      })
      .catch((error: unknown) => {
        showChannelRequestErrorToast('Error while adding event', error);
      })
      .then(() => {
        setState({
          type: dialogActionTypes.SET_SAVING_DATA,
          payload: false,
        });
        closeDialog();
        issueClient.sync();
        resync({ force: true });
      })
      .then(() =>
        managePermissions({
          eventDescriptionWithMarkup,
          issueManagers: availableUsers,
          issue,
          createDialog,
          organizationId,
        }).catch(() => {})
      );
  };

  useEffect(() => {
    if (isEditingEvent(action)) {
      prepareForEdit(action.params);
    } else {
      prepareForCreation(action.params);
    }

    //although we have a lot of deps, this effect will run only on mount
    //prepareForEdit is constant
    //action - this dep is provided from parent on mount and does not change until unmount
  }, [prepareForEdit, prepareForCreation, action]);

  const actionTypes = {
    [EventDialogActionNames.create]: {
      action: addNewEvent,
      cancelLabel: formatTranslation(intl, CommonTranslationKeys.cancel,),
      saveLabel: formatTranslation(intl, IssueTranslationKeys.eventsAddLabel),
    },
    [EventDialogActionNames.edit]: {
      action: editCurrentEvent,
      cancelLabel: formatTranslation(intl, CommonTranslationKeys.cancel,),
      saveLabel: formatTranslation(intl, CommonTranslationKeys.update,),
    },
  };

  const isTitleValid = useMemo(() => {
    if (!eventTitle) {
      if (isCreatingEvent(action)) {
        // because we use placeholder if title is empty
        return true;
      }
      return false;
    }
    return eventTitle.length <= MAX_EVENT_TITLE_LENGTH;
  }, [eventTitle, action]);

  const formReady =
    isTitleValid && !descriptionError && !GU.loading && !savingData;

  const errorMessage = useMemo(() => {
    return (
      nameTouched &&
      validateTitleInput(eventTitle, intl, action)
    );
  }, [eventTitle, intl, nameTouched, action]);

  return (
    <Dialog
      ref={dialogRootElementRef}
      open={isOpen}
      onClose={handleCancelAddEvent}
      PaperProps={{ className: classes.dialogWrapper }}
      aria-labelledby='customized-dialog-title'
      data-qa='add-event-dialog'
      sx={{
        zIndex: 1199,
        '.MuiPaper-root': {
          xs: {
            maxWidth: '90%',
            marginTop: '72px',
          },
          sm: { maxWidth: '80%', marginTop: 0, marginLeft: '64px' },
          md: { top: 0 },
        },
      }}
    >
      <Wrappers.DialogTitle
        label={dialogTitle}
        classes={classes}
        onClose={handleCancelAddEvent}
      />
      <DialogContent>
        <div className={classes.mainContent}>
          <Wrappers.NameInput
            classes={classes}
            charCount={charCount}
            value={eventTitle}
            maxLength={MAX_EVENT_TITLE_LENGTH}
            labelId={'issue_events_dialog_add_title'}
            errorMessage={errorMessage}
            onBlur={handleNameBlur}
            onChange={handleNameInputChange}
          />
          <DescriptionInputWithMentions
            key={descriptionResetKey}
            intl={intl}
            defaultDescription={eventDescriptionWithMarkup}
            classes={classes}
            onBlur={handleDescriptionBlur}
            onEscPress={handleCancelAddEvent}
            currentUser={currentUser}
            availableUsers={availableUsers}
            dialogRootElementRef={dialogRootElementRef}
            onChange={handleDescriptionChange}
            maxLength={MAX_EVENT_DESCRIPTION_LENGTH}
            onError={handleDescriptionError}
            error={descriptionError}
          />
        </div>
        <GraphicUploader {...GU} />
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <ButtonText
          onClick={handleCancelAddEvent}
          data-qa='event-dialog-cancel-button'
        >
          {actionTypes[action.actionName]?.cancelLabel || ''}
        </ButtonText>
        <ButtonContained
          disabled={!formReady}
          data-qa='event-dialog-add-button'
          onClick={actionTypes[action.actionName]?.action}
          endIcon={
            savingData ? (
              <CircularProgress
                size={13}
                thickness={4}
                disableShrink
                color='secondary'
              />
            ) : undefined
          }
        >
          {actionTypes[action.actionName]?.saveLabel || ''}
        </ButtonContained>
      </DialogActions>
    </Dialog>
  );
};

export default EventDialog;
